import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type TenantId from '../tenant';

export interface Token {
  sub: string;
  username: string;
  email: string;
  groups: string[] | null;
  roles: string[] | null;
  preferred_role: string;
  companyId: string;
  tenantId: TenantId;
}

export interface UserFeatureFlags {
  ports_license: boolean;
  cruise_license: boolean;
  fleet_license: boolean;
  ais_history: boolean;
  voyager_intel_tool: boolean;
  sanctions: boolean;
}

interface UserState {
  idToken: Token | null;
  idTokenError: string | null;
  liteMode: boolean;
  showLiteModePopup: boolean;
  userSigningOut: boolean;
  featureFlags: UserFeatureFlags | null;
}

export const LITE_MODE_LOCAL_STORAGE_KEY = 'lite_mode';

export const INITIAL_USER_STATE: UserState = {
  idToken: null,
  idTokenError: null,
  liteMode: JSON.parse(
    localStorage.getItem(LITE_MODE_LOCAL_STORAGE_KEY) || JSON.stringify(false)
  ),
  showLiteModePopup: false,
  userSigningOut: false,
  featureFlags: null,
};

const UserSlice = createSlice({
  name: 'User',
  initialState: INITIAL_USER_STATE,
  reducers: {
    setUserIdToken: (state, action) => {
      const token: Token = {
        sub: action.payload.sub,
        username: action.payload['cognito:username'],
        email: action.payload.email,
        groups: action.payload['cognito:groups'],
        roles: action.payload['cognito:roles'],
        preferred_role: action.payload.preferred_role, // Assign the value from object.preferred_role
        companyId: action.payload['custom:companyId'],
        tenantId: action.payload['custom:tenantId'],
      };
      state.idToken = token;
    },
    setUserIdTokenError: (state, action) => {
      state.idTokenError = action.payload;
    },
    setUserFeatureFlags: (
      state,
      action: PayloadAction<UserState['featureFlags']>
    ) => {
      if (!state.idToken) return;
      state.featureFlags = action.payload;
    },
    setLiteMode: (state, action: PayloadAction<UserState['liteMode']>) => {
      localStorage.setItem(
        LITE_MODE_LOCAL_STORAGE_KEY,
        JSON.stringify(action.payload)
      );
      state.liteMode = action.payload;
      // biome-ignore lint/style/noRestrictedGlobals: We're using this to reload the page
      location.reload();
    },
    setShowLiteModePopup: (
      state,
      action: PayloadAction<UserState['showLiteModePopup']>
    ) => {
      state.showLiteModePopup = action.payload;
    },
    setUserSigningOut: (state) => {
      state.userSigningOut = true;
    },
  },
});

export const {
  setUserIdToken,
  setUserIdTokenError,
  setUserFeatureFlags,
  setLiteMode,
  setShowLiteModePopup,
  setUserSigningOut,
} = UserSlice.actions;

export default UserSlice.reducer;
